/**
 * Capacitor / FCM tray taps do not always put chat ids on `notification.data`.
 * Android extras may nest, stringify JSON, or use PascalCase from older payloads.
 */
export function unwrapPushPayload(raw: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  visitPushValue(raw, out, 0);
  return out;
}

export function readPushField(data: Record<string, string>, ...keys: string[]): string {
  for (const key of keys) {
    const exact = data[key];
    if (exact != null && String(exact).trim() !== '') return String(exact).trim();

    const lower = data[key.toLowerCase()];
    if (lower != null && String(lower).trim() !== '') return String(lower).trim();

    const camel = key.charAt(0).toLowerCase() + key.slice(1);
    const camelVal = data[camel];
    if (camelVal != null && String(camelVal).trim() !== '') return String(camelVal).trim();

    const pascal = key.charAt(0).toUpperCase() + key.slice(1);
    const pascalVal = data[pascal];
    if (pascalVal != null && String(pascalVal).trim() !== '') return String(pascalVal).trim();
  }
  return '';
}

function visitPushValue(
  value: unknown,
  out: Record<string, string>,
  depth: number,
): void {
  if (value == null || depth > 5) return;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        visitPushValue(JSON.parse(trimmed) as unknown, out, depth + 1);
      } catch {
        // Not JSON — ignore.
      }
    }
    return;
  }

  if (typeof value !== 'object' || Array.isArray(value)) return;

  for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
    if (nested == null) continue;
    if (typeof nested === 'object') {
      visitPushValue(nested, out, depth + 1);
      continue;
    }
    if (typeof nested === 'string') {
      const trimmed = nested.trim();
      if (
        (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
        (trimmed.startsWith('[') && trimmed.endsWith(']'))
      ) {
        try {
          visitPushValue(JSON.parse(trimmed) as unknown, out, depth + 1);
          continue;
        } catch {
          // Keep the raw string below.
        }
      }
      out[key] = nested;
      const camel = key.charAt(0).toLowerCase() + key.slice(1);
      if (!(camel in out)) out[camel] = nested;
      continue;
    }

    if (typeof nested === 'number' || typeof nested === 'boolean') {
      const str = String(nested);
      out[key] = str;
      const camel = key.charAt(0).toLowerCase() + key.slice(1);
      if (!(camel in out)) out[camel] = str;
    }
  }
}
